import { Big } from 'big.js';
import { match } from 'ts-pattern';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { CONTENT_WRITING_PAY, CONTENT_WRITING_RATE } from '~/tenants/bre/performable/common';
import BreBookletsConfig, { BookletPaper } from '~/tenants/bre/performable/print/booklets/BreBookletsConfig';
import { BreBookletsPay, BreBookletsRate } from '~/tenants/bre/performable/print/booklets/BreBookletsData';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';
import BrePhotosConfig from '~/tenants/bre/performable/photos/BrePhotosConfig';
import { datetime } from '~/lib/datettime';

interface PageTypeOptions {
  paper: BookletPaper;
}

export default class BreBookletsJob extends BreJob<typeof BreBookletsConfig> {
  get performable(): typeof BreBookletsConfig {
    return BreBookletsConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.PROPERTY_MKTG);
  }

  get assignee(): string | null {
    return BreProviderId.PRINT_TEAM.toString();
  }

  get dependencies(): string[] {
    return [BrePhotosConfig.id];
  }

  get paperType(): string {
    const { paper } = this.metadata;
    return BreBookletsJob.pageType({ paper });
  }

  static pageType({ paper }: PageTypeOptions): string {
    return match(paper)
      .with('low_gloss', () => 'Heavy-gloss outside (with free UV), low-gloss inside')
      .with('heavy_gloss', () => 'Heavy-gloss outside (with free UV), heavy-gloss inside')
      .exhaustive();
  }

  isDelivery(): boolean {
    return true;
  }

  isPrint(): boolean {
    return true;
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreBookletsConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      sides: '8',
      count: '50',
      paper: 'low_gloss',
      need_by: datetime(this.order.created, this.order.timezone).toSQLDate()
    };
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Sides',
      value: this.metadata.sides,
      provider: true,
    });

    info.push({
      name: 'Count',
      value: this.metadata.count,
      provider: true,
    });

    info.push({
      name: 'Paper',
      value: this.paperType,
      provider: true,
    });

    info.push({
      name: 'Content Writing',
      value: this.metadata.content_writing ? 'Yes' : 'No',
      provider: true,
    });

    if (this.metadata.no_free_uv) {
      info.push({
        name: 'UV Opt-out',
        value: 'Yes',
        provider: true
      });
    }

    info.push({
      name: 'Need By',
      value: datetime(this.metadata.need_by, this.order.location.timezone).toFormat('LLL dd, yyyy'),
      provider: true,
    });

    return info;
  }

  revenueLines(): TenantJobLine[] {
    const { count, paper, sides, content_writing } = this.metadata;
    const rate = BreBookletsRate[sides]?.[paper]?.[count];
    const lines: TenantJobLine[] = [];

    if (rate) {
      lines.push({
        id: 'booklets_print',
        description: `${count} Pages ${this.paperType} (x${count})`,
        amount: new Big(rate),
        taxable: true,
      });
    }

    if (content_writing) {
      lines.push({
        id: 'content_writing',
        description: 'Content Writing',
        amount: new Big(CONTENT_WRITING_RATE),
      });
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { count, paper, sides, content_writing } = this.metadata;
    const pay = BreBookletsPay[sides]?.[paper]?.[count];
    const lines: TenantJobLine[] = [];

    if (pay) {
      lines.push({
        id: 'booklets_print',
        description: `${count} Pages ${this.paperType} (x${count})`,
        amount: new Big(pay),
      });
    }

    if (content_writing) {
      lines.push({
        id: 'content_writing',
        description: 'Content Writing',
        amount: new Big(CONTENT_WRITING_PAY),
      });
    }

    return lines;
  }
}
